import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';

// DRAFT legal text – must be reviewed by a solicitor before public launch (see CLAUDE.md).
// KCL's company details; contact address support@kcl.cloud.

const UPDATED = '14 October 2026';

function Page({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="h-full overflow-y-auto bg-[var(--bg)]">
      <article className="mx-auto max-w-3xl px-6 py-12 leading-relaxed [&_h2]:mb-2 [&_h2]:mt-8 [&_h2]:text-xl [&_h2]:font-semibold [&_li]:ml-5 [&_li]:list-disc [&_p]:mb-3 [&_ul]:mb-3">
        <a href="/" className="muted mb-8 inline-flex items-center gap-2 text-sm hover:underline">
          <ArrowLeft className="h-4 w-4" aria-hidden /> Back to KCL Canvas
        </a>
        <h1 className="text-3xl font-semibold">{title}</h1>
        <p className="muted mt-1 text-sm">Last updated {UPDATED} · Draft pending legal review</p>
        <div className="mt-8">{children}</div>
      </article>
    </div>
  );
}

export function PrivacyPolicy() {
  return (
    <Page title="Privacy Policy">
      <p>
        KCL Canvas (“we”, “us”) is provided by Kirkpatrick Consult Limited (company number 06285045, registered in England and Wales), 28 New Barns Road, Ely, Cambridgeshire, CB7 4PN, United Kingdom. We are the data controller for personal data processed through
        canvas.kcl.cloud and the KCL Canvas apps. Contact: <a href="mailto:support@kcl.cloud">support@kcl.cloud</a>.
      </p>
      <h2>What we collect</h2>
      <ul>
        <li>
          <strong>Account details</strong> – your email address and a securely hashed password, or your Microsoft account
          identifier if you sign in with Microsoft.
        </li>
        <li>
          <strong>Dashboard content</strong> – your settings, layouts, to-do and shopping lists, news feed and calendar
          addresses, and links to the photo folders you choose.
        </li>
        <li>
          <strong>Connected-account tokens</strong> – when you connect a cloud account we store access tokens, encrypted
          (AES-256), only to read the photos, videos, calendars and lists you select. We do not copy your photo library.
        </li>
        <li>
          <strong>Purchases</strong> – Stripe processes payments. We receive confirmation of payment and a customer reference,
          never your card details.
        </li>
        <li>
          <strong>Technical data</strong> – server logs and performance diagnostics (IP address, browser type, errors), kept for
          up to 30 days to run and secure the service.
        </li>
      </ul>
      <h2>Without an account</h2>
      <p>
        If you don’t sign in, your settings stay in your browser’s local storage on that device. Files you add from your device
        are displayed locally and are never uploaded.
      </p>
      <h2>Why we use it (lawful bases)</h2>
      <ul>
        <li>To provide the service you asked for – performance of a contract.</li>
        <li>To process your licence purchase and keep financial records – contract and legal obligation.</li>
        <li>To keep the service secure and working – our legitimate interests.</li>
      </ul>
      <p>
        We do not sell your data. The free tier’s ads are for KCL and its own brands (such as El Tiquetero): they are not
        personalised and do not use tracking.
      </p>
      <h2>Who we share it with</h2>
      <ul>
        <li>Microsoft Azure (hosting and database, United Kingdom region).</li>
        <li>Stripe (payments).</li>
        <li>
          Fasthosts (kcl.cloud web hosting): your screen loads our demo video from kcl.cloud, so Fasthosts sees your IP address.
        </li>
        <li>
          Open-Meteo (weather): your browser requests the forecast directly using the location you set; Open-Meteo sees your
          IP address.
        </li>
        <li>
          Feed and calendar publishers: our server fetches the feed and calendar addresses you add, so the publisher sees our
          server, not you.
        </li>
        <li>The cloud providers you connect, to read the content you choose.</li>
      </ul>
      <h2>How long we keep it</h2>
      <p>
        Account data is kept while your account is open. Deleting your account removes your settings, lists and connected-account
        tokens immediately. Purchase records are kept for the period required by tax law.
      </p>
      <h2>Your rights</h2>
      <p>
        Under UK and EU data protection law you can ask to access, correct, delete or export your data, and object to or
        restrict processing. Contact us at <a href="mailto:support@kcl.cloud">support@kcl.cloud</a>. You can also complain to the UK Information Commissioner’s
        Office (ico.org.uk) or your local supervisory authority.
      </p>
      <h2>Children</h2>
      <p>KCL Canvas accounts are for people aged 16 and over.</p>
      <h2>Changes</h2>
      <p>We will post any changes here and update the date above. Significant changes will be notified in the app.</p>
    </Page>
  );
}

export function TermsOfService() {
  return (
    <Page title="Terms of Service">
      <p>
        These terms apply to KCL Canvas, provided by Kirkpatrick Consult Limited (company number 06285045, registered in England and Wales), 28 New Barns Road, Ely, Cambridgeshire, CB7 4PN, United Kingdom (“KCL”). By using KCL Canvas you agree to them.
      </p>
      <h2>The service</h2>
      <p>
        KCL Canvas displays your own photos, videos, calendars, lists, weather and news on your screens. All features are free.
        The free tier shows a short advertisement for KCL or one of its brands from time to time while you watch.
      </p>
      <h2>Lifetime licence</h2>
      <ul>
        <li>A one-off payment of £10, plus VAT or other sales tax where it applies (shown at checkout), removes advertisements for your account on all your screens.</li>
        <li>“Lifetime” means for as long as KCL operates KCL Canvas. It is personal to your account and not transferable.</li>
        <li>
          The licence is digital content that starts as soon as you pay. At checkout you ask for it to start straight away
          and confirm that you understand you then lose the 14-day right to cancel that would otherwise apply. This doesn’t
          affect your other statutory rights: if the licence doesn’t work as described, contact{' '}
          <a href="mailto:support@kcl.cloud">support@kcl.cloud</a> and we’ll put it right or refund you.
        </li>
      </ul>
      <h2>Your content</h2>
      <p>
        You keep all rights to your content. You give us permission to access and display it only to provide the service to
        you. You must have the right to display any content you add.
      </p>
      <h2>Acceptable use</h2>
      <p>
        Don’t use KCL Canvas to break the law, to access other people’s accounts or content without permission, or to disrupt
        the service (including by using the feed fetcher to probe other systems).
      </p>
      <h2>Third-party services</h2>
      <p>
        Connected services (such as Microsoft, Stripe, news and calendar publishers, Open-Meteo and MeteoAlarm) are governed by their own terms. We
        are not responsible for their availability or content.
      </p>
      <h2>Availability and changes</h2>
      <p>
        We aim to keep KCL Canvas running smoothly but cannot guarantee it will always be available or error-free. We may change
        or discontinue features with reasonable notice.
      </p>
      <h2>Liability</h2>
      <p>
        Nothing in these terms limits liability for death or personal injury caused by negligence, fraud, or anything else
        that cannot be limited by law. Otherwise, our total liability to you is limited to the amount you paid us in the 12
        months before the claim. Your statutory consumer rights are not affected.
      </p>
      <h2>Ending your account</h2>
      <p>You can delete your account at any time. We may suspend accounts that break these terms.</p>
      <h2>Law</h2>
      <p>
        These terms are governed by the laws of England and Wales. If you live elsewhere in the UK or EU you keep the
        protection of your local consumer law and can bring claims in your local courts.
      </p>
      <h2>Contact</h2>
      <p>
        <a href="mailto:support@kcl.cloud">support@kcl.cloud</a> · Kirkpatrick Consult Limited (company number 06285045, registered in England and Wales), 28 New Barns Road, Ely, Cambridgeshire, CB7 4PN, United Kingdom
      </p>
    </Page>
  );
}
